import { FontFamily } from 'types/font.types';

export const MIN_FONT_SIZE = 8;
export const MAX_FONT_SIZE = 400;

// Common font size to appear in dropdown on FontSizeSelect component
export const COMMON_FONT_SIZES = [8, 10, 12, 14, 16, 20, 24, 30, 36, 42, 50, 64, 72, 96, 144, 200];

// Font sizes to appear in DropdownFontSizeSelect component
export const FONT_SIZES = [...Array(MAX_FONT_SIZE - MIN_FONT_SIZE)].map((_, i) => i + MIN_FONT_SIZE);

const MIN_LINE_HEIGHT = 0.8;
const MAX_LINE_HEIGHT = 3;
const LINE_HEIGHT_STEP = 0.1;

export const TEXT_LINE_HEIGHT = [...Array(Math.floor((MAX_LINE_HEIGHT - MIN_LINE_HEIGHT) * 10))].map((_, i) =>
  (MIN_LINE_HEIGHT + LINE_HEIGHT_STEP * i).toFixed(1),
);

export enum TEXT_ALIGNMENT {
  left = 'left',
  center = 'center',
  right = 'right',
}

export enum VERTICAL_ALIGNMENT {
  top = 'top',
  middle = 'middle',
  bottom = 'bottom',
}

type FontFamilyConfig = {
  // If the font natively has italic or bold support
  hasItalic: boolean;
  hasBold: boolean;

  // optional displayName for non-english fonts
  displayName?: string;
};

// Each font family in the editor should have a config
type FontFamilyConfigMap = { [key in FontFamily]: FontFamilyConfig };

export const FONT_FAMILY_CONFIG: FontFamilyConfigMap = {
  Abel: { hasItalic: false, hasBold: false },
  'Abril Fatface': { hasItalic: false, hasBold: false },
  Aldrich: { hasItalic: false, hasBold: false },
  Alegreya: { hasItalic: true, hasBold: true },
  'Alfa Slab One': { hasItalic: false, hasBold: false },
  'Allerta Stencil': { hasItalic: false, hasBold: false },
  Amaranth: { hasItalic: true, hasBold: true },
  'Amatic SC': { hasItalic: false, hasBold: true },
  'Anonymous Pro': { hasItalic: true, hasBold: true },
  'Antic Slab': { hasItalic: false, hasBold: false },
  Anton: { hasItalic: false, hasBold: false },
  'Archivo Black': { hasItalic: false, hasBold: false },
  'Archivo Narrow': { hasItalic: true, hasBold: true },
  Arial: { hasItalic: true, hasBold: true },
  Arimo: { hasItalic: true, hasBold: true },
  Arvo: { hasItalic: true, hasBold: true },
  Asap: { hasItalic: true, hasBold: true },
  Assistant: { hasItalic: false, hasBold: true },
  'Averia Libre': { hasItalic: true, hasBold: true },
  'Averia Sans Libre': { hasItalic: true, hasBold: true },
  'Averia Serif Libre': { hasItalic: true, hasBold: true },
  'Baloo Thambi': { hasItalic: false, hasBold: false },
  Bangers: { hasItalic: false, hasBold: false },
  'Barlow Condensed': { hasItalic: true, hasBold: true },
  'Bebas Neue': { hasItalic: false, hasBold: false },
  'Berkshire Swash': { hasItalic: false, hasBold: false },
  'Bodoni Moda': { hasItalic: true, hasBold: true },
  'Bungee Hairline': { hasItalic: false, hasBold: false },
  'Bungee Outline': { hasItalic: false, hasBold: false },
  'Bungee Shade': { hasItalic: false, hasBold: false },
  Cabin: { hasItalic: true, hasBold: true },
  'Cabin Sketch': { hasItalic: false, hasBold: true },
  Cairo: { hasItalic: false, hasBold: true },
  'Cedarville Cursive': { hasItalic: false, hasBold: false },
  Chewy: { hasItalic: false, hasBold: false },
  Cinzel: { hasItalic: false, hasBold: true },
  Codystar: { hasItalic: false, hasBold: false },
  Comfortaa: { hasItalic: false, hasBold: true },
  'Comic Sans MS': { hasItalic: false, hasBold: true },
  'Contrail One': { hasItalic: false, hasBold: false },
  Cookie: { hasItalic: false, hasBold: false },
  Courgette: { hasItalic: false, hasBold: false },
  'Courier New': { hasItalic: true, hasBold: true },
  Coustard: { hasItalic: false, hasBold: false },
  'Crimson Text': { hasItalic: true, hasBold: true },
  'Dancing Script': { hasItalic: false, hasBold: true },
  'Didact Gothic': { hasItalic: false, hasBold: false },
  Diplomata: { hasItalic: false, hasBold: false },
  'DM Serif Text': { hasItalic: true, hasBold: false },
  Dosis: { hasItalic: false, hasBold: true },
  'EB Garamond': { hasItalic: true, hasBold: true },
  Economica: { hasItalic: true, hasBold: true },
  'Elsie Swash Caps': { hasItalic: false, hasBold: false },
  Epilogue: { hasItalic: true, hasBold: true },
  Exo: { hasItalic: true, hasBold: true },
  'Expletus Sans': { hasItalic: true, hasBold: true },
  'Fascinate Inline': { hasItalic: false, hasBold: false },
  'Fjalla One': { hasItalic: false, hasBold: false },
  'Fredericka the Great': { hasItalic: false, hasBold: false },
  'Fredoka One': { hasItalic: false, hasBold: false },
  'Glass Antiqua': { hasItalic: false, hasBold: false },
  Graduate: { hasItalic: false, hasBold: false },
  'Grand Hotel': { hasItalic: false, hasBold: false },
  'Great Vibes': { hasItalic: false, hasBold: false },
  'Happy Monkey': { hasItalic: false, hasBold: false },
  'Headland One': { hasItalic: false, hasBold: false },
  Heebo: { hasItalic: false, hasBold: true },
  'Indie Flower': { hasItalic: false, hasBold: false },
  Inter: { hasItalic: false, hasBold: true },
  Itim: { hasItalic: false, hasBold: false },
  'Josefin Sans': { hasItalic: true, hasBold: true },
  'Josefin Slab': { hasItalic: true, hasBold: true },
  'Julius Sans One': { hasItalic: false, hasBold: false },
  'Just Another Hand': { hasItalic: false, hasBold: false },
  Kalam: { hasItalic: false, hasBold: true },
  Karla: { hasItalic: true, hasBold: true },
  Karma: { hasItalic: false, hasBold: true },
  'Kaushan Script': { hasItalic: false, hasBold: false },
  Knewave: { hasItalic: false, hasBold: false },
  'Kumar One': { hasItalic: false, hasBold: false },
  Lalezar: { hasItalic: false, hasBold: false },
  Lato: { hasItalic: true, hasBold: true },
  'Lexend Exa': { hasItalic: false, hasBold: true },
  'Lexend Giga': { hasItalic: false, hasBold: true },
  'Lexend Mega': { hasItalic: false, hasBold: true },
  'Lexend Peta': { hasItalic: false, hasBold: true },
  'Lexend Tera': { hasItalic: false, hasBold: true },
  'Lexend Zetta': { hasItalic: false, hasBold: true },
  'Libre Baskerville': { hasItalic: true, hasBold: true },
  'Libre Franklin': { hasItalic: true, hasBold: true },
  Limelight: { hasItalic: false, hasBold: false },
  'Lobster Two': { hasItalic: true, hasBold: true },
  Lora: { hasItalic: true, hasBold: true },
  Lustria: { hasItalic: false, hasBold: false },
  'Maven Pro': { hasItalic: false, hasBold: true },
  Merriweather: { hasItalic: true, hasBold: true },
  Mitr: { hasItalic: false, hasBold: true },
  Monoton: { hasItalic: false, hasBold: false },
  Montserrat: { hasItalic: true, hasBold: true },
  'Ms Madi': { hasItalic: false, hasBold: false },
  Mukta: { hasItalic: false, hasBold: true },
  Muli: { hasItalic: true, hasBold: true },
  'News Cycle': { hasItalic: false, hasBold: true },
  'Nixie One': { hasItalic: false, hasBold: false },
  'Noto Serif': { hasItalic: true, hasBold: true },
  Nunito: { hasItalic: true, hasBold: true },
  'Old Standard TT': { hasItalic: true, hasBold: true },
  'Open Sans': { hasItalic: true, hasBold: true },
  Oswald: { hasItalic: false, hasBold: true },
  Overlock: { hasItalic: true, hasBold: true },
  Oxygen: { hasItalic: false, hasBold: true },
  Pacifico: { hasItalic: false, hasBold: false },
  Palanquin: { hasItalic: false, hasBold: true },
  Parisienne: { hasItalic: false, hasBold: false },
  'Pathway Gothic One': { hasItalic: false, hasBold: false },
  'Patrick Hand': { hasItalic: false, hasBold: false },
  Pattaya: { hasItalic: false, hasBold: false },
  'Paytone One': { hasItalic: false, hasBold: false },
  'Permanent Marker': { hasItalic: false, hasBold: false },
  'Petit Formal Script': { hasItalic: false, hasBold: false },
  Philosopher: { hasItalic: true, hasBold: true },
  'Pinyon Script': { hasItalic: false, hasBold: false },
  Playball: { hasItalic: false, hasBold: false },
  'Playfair Display': { hasItalic: true, hasBold: true },
  'Poiret One': { hasItalic: false, hasBold: false },
  Poppins: { hasItalic: true, hasBold: true },
  Prompt: { hasItalic: true, hasBold: true },
  'PT Sans': { hasItalic: true, hasBold: true },
  'PT Sans Narrow': { hasItalic: false, hasBold: true },
  'PT Serif': { hasItalic: true, hasBold: true },
  Quantico: { hasItalic: true, hasBold: true },
  Questrial: { hasItalic: false, hasBold: false },
  Quicksand: { hasItalic: false, hasBold: true },
  Raleway: { hasItalic: true, hasBold: true },
  'Raleway Dots': { hasItalic: false, hasBold: false },
  'Ribeye Marrow': { hasItalic: false, hasBold: false },
  Roboto: { hasItalic: true, hasBold: true },
  'Roboto Condensed': { hasItalic: true, hasBold: true },
  'Roboto Slab': { hasItalic: false, hasBold: true },
  Rochester: { hasItalic: false, hasBold: false },
  'Rozha One': { hasItalic: false, hasBold: false },
  Rubik: { hasItalic: true, hasBold: true },
  'Rubik Mono One': { hasItalic: false, hasBold: false },
  Rye: { hasItalic: false, hasBold: false },
  Sail: { hasItalic: false, hasBold: false },
  Satisfy: { hasItalic: false, hasBold: false },
  Share: { hasItalic: true, hasBold: true },
  Shrikhand: { hasItalic: false, hasBold: false },
  Simonetta: { hasItalic: true, hasBold: false },
  Sintony: { hasItalic: false, hasBold: true },
  'Six Caps': { hasItalic: false, hasBold: false },
  Solway: { hasItalic: false, hasBold: true },
  Sora: { hasItalic: false, hasBold: true },
  'Source Sans Pro': { hasItalic: true, hasBold: true },
  'Space Grotesk': { hasItalic: false, hasBold: true },
  Spectral: { hasItalic: true, hasBold: true },
  'Sue Ellen Francisco': { hasItalic: false, hasBold: false },
  'Times New Roman': { hasItalic: true, hasBold: true },
  'Titillium Web': { hasItalic: true, hasBold: true },
  'Trebuchet MS': { hasItalic: true, hasBold: true },
  Trirong: { hasItalic: true, hasBold: true },
  Ubuntu: { hasItalic: true, hasBold: true },
  'Ubuntu Mono': { hasItalic: true, hasBold: true },
  Ultra: { hasItalic: false, hasBold: false },
  'Unica One': { hasItalic: false, hasBold: false },
  Unkempt: { hasItalic: false, hasBold: true },
  'Varela Round': { hasItalic: false, hasBold: false },
  'Vast Shadow': { hasItalic: false, hasBold: false },
  Verdana: { hasItalic: true, hasBold: true },
  Vidaloka: { hasItalic: false, hasBold: false },
  Voltaire: { hasItalic: false, hasBold: false },
  VT323: { hasItalic: false, hasBold: false },
  'Wire One': { hasItalic: false, hasBold: false },
  'Work Sans': { hasItalic: true, hasBold: true },
  Yellowtail: { hasItalic: false, hasBold: false },
  'Yeseva One': { hasItalic: false, hasBold: false },
  Amiri: { hasItalic: true, hasBold: true, displayName: 'أميري' },
  'Fira Sans': { hasItalic: true, hasBold: true, displayName: 'Фира Санс' },
  Kanit: { hasItalic: true, hasBold: true, displayName: 'คณิต' },
  Lateef: { hasItalic: false, hasBold: true, displayName: 'لطيف' },
  Mirza: { hasItalic: false, hasBold: true, displayName: 'ميرزا' },
  'Noto Sans SC': { hasItalic: false, hasBold: true, displayName: '諾托' },
  Scheherazade: { hasItalic: false, hasBold: true, displayName: 'شهرزاد' },
  Tajawal: { hasItalic: false, hasBold: true, displayName: 'تجوّل' },
  'Noto Serif Ethiopic': { hasItalic: false, hasBold: true, displayName: 'ኖቶ ሰሪፍ' },
};

export const WIDGET_DEFAULT_FONT = 'Roboto';
